import React, { useEffect, useState, useRef } from 'react'
import { useIntl } from 'react-intl'
import { ErrorMessage, Field } from 'formik'
import { DisplayImage } from '../../widgets/components/General/DisplayImage'
import { ToolTipUI } from '../../widgets/components/UI/ToolTipUI'
import debounce from 'lodash/debounce'
import Skeleton from 'react-loading-skeleton'
import { useThemeMode } from '../../../../_metronic/partials'

export default function UserSearchTextInput({
  onSelect,
  onNewEntryButtonClick,
  formik,
  dropdownData,
  fieldName,
  label,
  placeholder,
  isDisabled = false,
  openUserDropdown,
  dropdownDataLoading,
  setOpenUserDropdown,
  fieldClicked,
  toolTipText,
  isMargin,
  ariaLabel,
  onFocus,
  onBlur,
}: any) {
  const { formatMessage } = useIntl();
  const { mode } = useThemeMode()
  // Use formik.values to track search input instead of separate state
  const [filteredData, setFilteredData] = useState<any[]>(dropdownData);

  useEffect(() => {
    setFilteredData(dropdownData);
  }, [dropdownData]);

  const debouncedUpdateSearchQuery = debounce((value: string) => {
    if (value) {
      const results = dropdownData?.filter(
        (user: any) =>
          user?.firstName?.toLowerCase()?.includes(value.toLowerCase()) ||
          user?.lastName?.toLowerCase()?.includes(value.toLowerCase())
      );
      setFilteredData(results);
    } else {
      setFilteredData(dropdownData);
    }
  }, 300);

  const handleInputChange = (event: any) => {
    const value = event.target.value;
    formik.setFieldValue(fieldName, value); 
    debouncedUpdateSearchQuery(value);
  };

  const handleUserClick = (user: any) => {
    formik.setFieldValue(fieldName, `${user.firstName} ${user.lastName}`);
    onSelect(user.individualId);
    setOpenUserDropdown(false);
  };

  const dropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenUserDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={`fv-row col-12 ${isMargin || 'mb-8'}`}>
      <div className='d-flex justify-content-between'>
        <label className='form-label font-size-13 text-dark text-capitalize ms-md-0'>
          {label}* <ToolTipUI tooltipText={toolTipText} />
        </label>
      </div>
      <div className='position-relative d-md-flex flex-md-column w-100'>
        <Field
          className={`form-control h-40px font-size-12 ${formik.touched[fieldName] && formik.errors[fieldName]
            ? 'is-invalid-local'
            : 'is-valid-local'
            }`}
          type='text'
          name={fieldName}
          value={formik.values[fieldName]} // Always rely on formik values
          onFocus={onFocus}
          onBlur={onBlur}
          onChange={handleInputChange}
          disabled={isDisabled}
          placeholder={placeholder}
          aria-label={ariaLabel}
        />

        {/* Dropdown with matching users */}
        {openUserDropdown && fieldClicked === ariaLabel && (
          <div className={`mt-2 ${mode === 'dark' ? 'user-dropdown-dark' : 'user-dropdown'}`} ref={dropdownRef}>
            {dropdownDataLoading ? (
              <div className='w-100 mt-0'>
                <Skeleton
                  height={150}
                  baseColor='var(--kt-skeleton-bg-color)'
                  highlightColor='var(--kt-skeleton-highlight-bg-color)'
                />
              </div>
            ) : (
              <div className={mode === 'dark' ? 'user-dropdown-content-dark' : 'user-dropdown-content'}>
                {filteredData?.length > 0 ? (
                  filteredData.map((user: any) => (
                    <div
                      className={`d-flex flex-row align-items-center ${mode === 'dark' ? 'user-info-dark' : 'user-info'}`}
                      onClick={() => handleUserClick(user)}
                      key={user.individualId}
                    >
                      <DisplayImage
                        imgName={user?.profileImage?.name}
                        height='20px'
                        width='20px'
                        fit='contain'
                        alt='profile'
                      />
                      <div className='user-names'>
                        <p className='first-name mb-0 ms-3'>
                          {user.firstName} {user.lastName}
                        </p>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className='no-results'>
                    <p>
                      {formatMessage({
                        id: 'No match found. Continue typing to create a new entry.',
                      })}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* New Entry button inside dropdown */}
            <hr />
            <button
              className='btn btn-primary mt-2 w-100'
              onClick={() => {
                setOpenUserDropdown(false);
                onNewEntryButtonClick();
              }}
            >
              {formatMessage({ id: 'New Entry' })}
            </button>
          </div>
        )}
      </div>
      <div className='text-danger mt-2'>
        {typeof formik.errors[fieldName] === 'string' && <ErrorMessage name={fieldName} />}
      </div>
    </div>
  );
}

